import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider
} from '@mui/material';
import {
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  PlayArrow as StartIcon,
  Block as BlockIcon,
  NoteAdd as NoteAddIcon
} from '@mui/icons-material';

/**
 * Menu component for task actions
 * @param {Object} props - Component props
 * @param {HTMLElement} props.anchorEl - Element to anchor menu to
 * @param {Object} props.selectedTile - The selected task tile
 * @param {Function} props.onClose - Function to close menu
 * @param {Function} props.onEdit - Function to edit task
 * @param {Function} props.onMarkAsDone - Function to mark task as complete
 * @param {Function} props.onRemove - Function to remove task
 * @param {Function} props.onDownloadPDF - Function to download as PDF
 * @param {Function} props.onDownloadDOCX - Function to download as DOCX
 * @param {Function} props.showNotification - Function to show notifications
 * @returns {React.ReactElement} Task menu component
 */
const TaskMenu = ({ 
  anchorEl, 
  selectedTile, 
  onClose, 
  onEdit, 
  onMarkAsDone, 
  onRemove,
  onDownloadPDF,
  onDownloadDOCX,
  showNotification
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Don't render if no tile is selected
  if (!selectedTile) return null;
  
  // Handle task progress update
  const handleStartTask = () => {
    try {
      // Logic for starting task here
      showNotification({
        message: `Started ${selectedTile.title}`,
        type: 'success'
      });
      onClose();
    } catch (error) {
      console.error('Error starting task:', error);
      showNotification({
        message: 'Failed to start task',
        type: 'error'
      });
    }
  };
  
  // Handle mark as done
  const handleMarkAsDone = () => {
    if (onMarkAsDone) {
      onMarkAsDone(selectedTile);
    }
    onClose();
  };
  
  // Handle delete confirmation dialog
  const openDeleteConfirm = () => {
    setShowDeleteConfirm(true);
    onClose();
  };
  
  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
  };
  
  const confirmDelete = () => {
    if (onRemove) {
      onRemove(selectedTile);
    }
    setShowDeleteConfirm(false);
  };
  
  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        PaperProps={{
          style: {
            width: 220,
            padding: '8px 0'
          }
        }}
      >
        {/* Task tile title as menu header */}
        <MenuItem disabled sx={{ opacity: 1, pb: 0 }}>
          <ListItemText 
            primary={
              <Typography 
                noWrap 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  color: 'text.primary'
                }}
              >
                {selectedTile?.title || 'Task'}
              </Typography>
            }
          />
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Edit task */}
        <MenuItem onClick={() => { onEdit && onEdit(selectedTile); onClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Task" />
        </MenuItem>
        
        {/* Start task (if not completed) */}
        {selectedTile?.status !== 'completed' && (
          <MenuItem onClick={handleStartTask}>
            <ListItemIcon>
              <StartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Start Task" />
          </MenuItem>
        )}
        
        {/* Mark as complete (if not completed) */}
        {selectedTile?.status !== 'completed' && (
          <MenuItem onClick={handleMarkAsDone}>
            <ListItemIcon>
              <CheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Mark as Completed" />
          </MenuItem>
        )}
        
        {/* Add implementation notes */}
        <MenuItem onClick={() => { onClose(); }}>
          <ListItemIcon>
            <NoteAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Add Notes" />
        </MenuItem>
        
        {/* View timeline (if completed) */}
        <MenuItem onClick={() => { onClose(); }}>
          <ListItemIcon>
            <TimelineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Timeline" />
        </MenuItem>
        
        {/* Download section */}
        <Divider sx={{ my: 1 }} />
        
        {/* Download as PDF */}
        <MenuItem onClick={() => { onDownloadPDF && onDownloadPDF(selectedTile); onClose(); }}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download as PDF" />
        </MenuItem>
        
        {/* Download as DOCX */}
        <MenuItem onClick={() => { onDownloadDOCX && onDownloadDOCX(selectedTile); onClose(); }}>
          <ListItemIcon>
            <DocIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download as DOCX" />
        </MenuItem>
        
        {/* Danger zone */}
        <Divider sx={{ my: 1 }} />
        
        {/* Remove task */}
        <MenuItem onClick={openDeleteConfirm} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Remove Task" />
        </MenuItem>
      </Menu>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            position: 'relative',
            top: '5vh',
            margin: '0 auto'
          }
        }}
      >
        <DialogTitle>Remove Task</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove "{selectedTile?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

TaskMenu.propTypes = {
  anchorEl: PropTypes.object,
  selectedTile: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onMarkAsDone: PropTypes.func,
  onRemove: PropTypes.func,
  onDownloadPDF: PropTypes.func,
  onDownloadDOCX: PropTypes.func,
  showNotification: PropTypes.func.isRequired
};

export default TaskMenu;